import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { formatBangkokLive } from '@/lib/dateFR';
import { localizeNotification } from '@/lib/notificationI18n';
import { stateForDashboardLink } from '@/lib/deepLinks';
import { NOTIFICATIONS_CHANGED } from '@/lib/notificationEvents';

// Admin notifications → the element they refer to in the admin dashboard, by
// its id (never by title or list position). Null: no specific element.
const adminLink = (n) => {
  const id = typeof n.entity_id === 'string' ? n.entity_id : '';
  if (n.type === 'new_service' && id) return `/dashboard?tab=services&service=${encodeURIComponent(id)}`;
  if (n.type === 'new_provider' && id) return `/dashboard?tab=providers&provider=${encodeURIComponent(id)}`;
  if (id === 'finance_center') return '/dashboard?tab=finances';
  return null;
};

export const NotificationBell = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  // Protection against stale answers: several refreshes can be in flight at once (poll, event, opening the
  // panel) and answers can come back in any order. Each request takes a number when it starts; its answer is
  // applied only if no answer from a LATER request has been applied already, only while the component is
  // mounted, and only for the account that asked (a change of account drops what was still in flight).
  const issued = useRef(0);
  const applied = useRef(0);
  const mounted = useRef(true);
  const accountId = useRef(user?.user_id);
  accountId.current = user?.user_id;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const fetchData = useCallback(async () => {
    if (!user) return;
    const ticket = ++issued.current;
    const asked = user.user_id;
    try {
      const [c, l] = await Promise.all([
        axios.get(`${API}/notifications/unread-count`, { withCredentials: true }),
        axios.get(`${API}/notifications?limit=20`, { withCredentials: true }),
      ]);
      if (!mounted.current || accountId.current !== asked || ticket < applied.current) return;   // stale: ignore
      applied.current = ticket;
      setUnread(c.data?.unread || 0);
      setItems(l.data || []);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchData();
    const t = setInterval(fetchData, 30000);
    // A conversation was just read (chat or booking window): update the counter now, not in 30 s.
    let pending;
    const refreshSoon = () => { clearTimeout(pending); pending = setTimeout(fetchData, 400); };
    window.addEventListener(NOTIFICATIONS_CHANGED, refreshSoon);
    return () => { clearInterval(t); clearTimeout(pending); window.removeEventListener(NOTIFICATIONS_CHANGED, refreshSoon); };
  }, [user, fetchData]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setItems((prev) => prev.map((i) => ({ ...i, is_read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    setOpen(false);
    // One row per conversation: it opens that conversation and reads NOTHING by itself;
    // the counter drops when the messages are really displayed (server side).
    if (n.type === 'message_group') {
      const target = typeof n.url === 'string' && n.url.startsWith('/dashboard?') ? n.url : `/dashboard?chat=${encodeURIComponent(n.entity_id)}`;
      navigate(target, { state: stateForDashboardLink(target, window.location.pathname) });
      return;
    }
    if (!n.is_read) {
      axios.put(`${API}/notifications/${n.notification_id}/read`, {}, { withCredentials: true }).catch(() => {});
      setUnread((u) => Math.max(u - 1, 0));
      setItems((prev) => prev.map((i) => (i.notification_id === n.notification_id ? { ...i, is_read: true } : i)));
    }
    // If notification refers to a booking, open the booking detail modal on the dashboard.
    const isBooking = typeof n.entity_id === 'string' && /^TM-\d{4}-\d+/.test(n.entity_id);
    if (isBooking) {
      // Prefer the server link when it targets this booking (e.g. a provider
      // account's own trip opens in "Mes réservations (voyageur)").
      const url = typeof n.url === 'string' && n.url.startsWith('/dashboard?') && n.url.includes(`booking=${n.entity_id}`) ? n.url : null;
      const target = url || `/dashboard?booking=${encodeURIComponent(n.entity_id)}`;
      // Already on the dashboard: the window it opens is one history step (« Retour » closes it).
      navigate(target, { state: stateForDashboardLink(target, window.location.pathname) });
      return;
    }
    // Settlement-related notifications → open the exact settlement detail
    // dialog on the current dashboard (reuses SettlementDetailDialog).
    const isSettlement = typeof n.entity_id === 'string' && /^SETTLE-/i.test(n.entity_id);
    if (isSettlement) {
      navigate(`/dashboard?tab=finances&settlement=${encodeURIComponent(n.entity_id)}`);
      return;
    }
    // Admin: new listing to validate, new provider, weekly settlements.
    const adminTarget = user?.role === 'admin' ? adminLink(n) : null;
    if (adminTarget) {
      navigate(adminTarget);
      return;
    }
    // Service-related notifications (validated / rejected / corrections needed / suspended / deleted by admin)
    // → open the corresponding listing directly in the Provider dashboard.
    // For corrections_needed we open the edit modal so the provider can act on the admin note.
    // For service_deleted_by_admin, route to the provider dashboard listings tab (annonces) to show context.
    const isServiceNotif = typeof n.type === 'string' && n.type.startsWith('service_') && typeof n.entity_id === 'string' && n.entity_id;
    if (isServiceNotif) {
      if (n.type === 'service_deleted_by_admin') {
        // The service has been removed by Admin. Navigate to the Annonces tab so the provider
        // sees their active listings (the deleted one will not appear in the default list).
        navigate(`/dashboard?tab=annonces`);
        return;
      }
      const param = n.type === 'service_corrections_needed' ? 'editService' : 'service';
      navigate(`/dashboard?${param}=${encodeURIComponent(n.entity_id)}`);
      return;
    }
    // "/admin" (older notifications) is not a page: the admin space is /dashboard.
    if (n.url) navigate(n.url === '/admin' ? '/dashboard' : n.url);
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) fetchData(); }}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
          aria-label={t('notifications.ariaLabel')}
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-primary" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse"
              data-testid="notif-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[calc(100vw-1.5rem)] sm:w-96 max-w-sm p-0 bg-white text-primary border border-slate-200 shadow-2xl rounded-xl z-[100]"
        data-testid="notif-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white rounded-t-xl">
          <DropdownMenuLabel className="p-0 text-sm font-semibold text-primary">{t('notifications.title')}</DropdownMenuLabel>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-accent hover:underline"
              data-testid="notif-mark-all-read"
            >
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>
        <div className="max-h-[70vh] sm:max-h-96 overflow-y-auto bg-white">
          {items.length === 0 ? (
            <div className="py-8 text-center text-sm text-ink-muted bg-white">
              {t('notifications.empty')}
            </div>
          ) : items.map((n) => {
            // Phase 2.4 — i18n keys with params; otherwise known server templates
            // are re-rendered in the current language; fallback to legacy title/body.
            if (n.type === 'message_group') {
              return (
                <button
                  key={n.notification_id}
                  onClick={() => openItem(n)}
                  className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-3 bg-accent/5"
                  data-testid={`notif-item-${n.notification_id}`}
                >
                  <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-accent" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm break-words text-primary font-semibold" data-testid="notif-conversation-title">
                      💬 {n.participants?.client || '—'} ↔ {n.participants?.provider || '—'}
                    </p>
                    <p className="text-xs font-semibold text-accent" data-testid="notif-conversation-count">
                      {t('notifications.messageGroup.unread', { count: n.unread_count })}
                    </p>
                    {n.body && <p className="text-xs text-ink-muted break-words line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-ink-muted mt-0.5">{formatBangkokLive(n.created_at).replace(' à ', ` ${t('bookingDetail.at')} `)}</p>
                  </div>
                </button>
              );
            }
            const { title: displayTitle, body: displayBody } = localizeNotification(n, t);
            return (
            <button
              key={n.notification_id}
              onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!n.is_read ? 'bg-accent/5' : 'bg-white'}`}
              data-testid={`notif-item-${n.notification_id}`}
            >
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.is_read ? 'bg-transparent' : 'bg-accent'}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm break-words ${n.is_read ? 'text-ink-light' : 'text-primary font-semibold'}`}>
                  {displayTitle}
                </p>
                {displayBody && <p className="text-xs text-ink-muted break-words line-clamp-3">{displayBody}</p>}
                {n.params?.action_key && (
                  <p className="text-xs font-semibold text-accent mt-0.5" data-testid={`notif-action-${n.notification_id}`}>{t(n.params.action_key)} →</p>
                )}
                <p className="text-[10px] text-ink-muted mt-0.5">{formatBangkokLive(n.created_at).replace(' à ', ` ${t('bookingDetail.at')} `)}</p>
              </div>
              {n.is_read && <Check className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
            </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
